/**
 * Toggle — headless proprietary primitive.
 *
 * A binary on/off control rendered as `<button role="switch">` with
 * `aria-checked`. Following WAI-ARIA Authoring Practices 1.3 §3.23
 * ("Switch Pattern") — a switch is keyboard-equivalent to a checkbox
 * (Space/Enter toggles, Tab navigates) but communicates a distinct
 * "on/off state of a single option" semantic to assistive tech.
 *
 * Client component (consumes `PermissionContext` + `AuditContext`).
 *
 * Contracts honored:
 *   - Permission gate via `can('toggle', resource, attrs)`. Denied
 *     presentations collapse like Button: `disabled` → native disabled,
 *     `readOnly` → visible + non-mutating, `hidden` → null.
 *   - Canonical `toggle:change` audit on state flip; `permission:denied`
 *     on blocked flip in readOnly mode.
 *   - `data-oshon-primitive="toggle"` + `data-permission-state` styling
 *     hooks; `data-state="on"|"off"` for the two states.
 *
 * Proprietary rather than Radix-wrapped because switch semantics are
 * narrow enough that a native `<button>` + `aria-checked` is a complete,
 * well-specified pattern. Same rubric as Button (ADR-001).
 */

'use client';

import { forwardRef, useState } from 'react';
import type {
  ButtonHTMLAttributes,
  MouseEventHandler,
  ReactNode,
  Ref,
} from 'react';
import {
  evaluatePermission,
  resolvePermissions,
  usePermissions,
  useAudit,
  type PermissionContext,
} from '../context.js';
import { CANONICAL_ACTIONS } from '../actions.js';

type NativeButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'type' | 'disabled' | 'role' | 'aria-checked' | 'aria-pressed'
>;

export interface ToggleProps extends NativeButtonProps {
  /**
   * Controlled on/off state. Mutually exclusive with `defaultChecked`.
   * When provided, the primitive is fully controlled — the consumer
   * manages state and responds to `onCheckedChange` to update it.
   */
  checked?: boolean;
  /**
   * Initial state for uncontrolled mode. Primitive owns its own state;
   * consumer reads it only via `onCheckedChange`.
   */
  defaultChecked?: boolean;
  /**
   * Fires whenever the switch flips. `next` is the new state. Not called
   * when a flip attempt is denied by permissions or blocked by
   * disabled/loading.
   */
  onCheckedChange?: (next: boolean) => void;
  /**
   * Disable the toggle. Renders with native `disabled` + `aria-disabled`.
   * No flip, no audit.
   */
  disabled?: boolean;
  /**
   * Visible label for assistive tech. Either pass `children` (the label
   * is read as the button's accessible name) or an `aria-label` / use an
   * external `<label htmlFor>` + `id`. For icon-only toggles, always use
   * `aria-label`.
   */
  children?: ReactNode;
  /** Per-instance permission override; merges over context. */
  permissions?: Partial<PermissionContext>;
  /**
   * Resource name for `permissions.can('toggle', resource, attrs)`.
   * Default `'toggle'`. Override for specific semantics, e.g.
   * `resource="settings:notifications"`.
   */
  resource?: string;
  /** Attribute bag for attribute-based access control. */
  permissionAttrs?: Record<string, unknown>;
}

/**
 * Headless switch/toggle. Accessible `<button role="switch">` with:
 *   - `aria-checked` synchronised with state
 *   - `disabled` + `aria-disabled` synchronised
 *   - Permission gating via `can('toggle', resource, attrs)` with three
 *     modes (disabled / readOnly / hidden)
 *   - Canonical `toggle:change` audit on flip; `permission:denied` on
 *     blocked flip in readOnly mode
 *   - `data-oshon-primitive="toggle"` + `data-state="on"|"off"` +
 *     `data-permission-state` styling hooks
 *   - No visual styling — consumers supply their own className / styles.
 *
 * Keyboard: native browser behavior — Enter + Space activate (flip).
 *
 * Controlled vs. uncontrolled: pass `checked` for controlled,
 * `defaultChecked` for uncontrolled. Only one at a time.
 */
export const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(
  function Toggle(
    {
      checked,
      defaultChecked = false,
      onCheckedChange,
      disabled = false,
      children,
      permissions,
      resource = 'toggle',
      permissionAttrs,
      onClick,
      ...rest
    }: ToggleProps,
    ref: Ref<HTMLButtonElement>,
  ) {
    const isControlled = checked !== undefined;
    const [internal, setInternal] = useState(defaultChecked);
    const current = isControlled ? (checked as boolean) : internal;

    const contextPermissions = usePermissions();
    const effective = resolvePermissions(permissions, contextPermissions);
    const mode = effective.mode ?? 'disabled';
    const emit = useAudit();

    const { granted, denyReason } = evaluatePermission(
      effective,
      'toggle',
      resource,
      permissionAttrs,
    );

    if (!granted && mode === 'hidden') return null;

    const permissionBlocks = !granted && mode !== 'readOnly';
    const isDisabled = disabled || permissionBlocks;

    const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
      if (disabled) return;

      if (!granted) {
        emit({
          component: 'toggle',
          action: CANONICAL_ACTIONS.PERMISSION_DENIED,
          permissionResult: 'denied',
          ...(denyReason ? { denyReason } : {}),
          details: { attemptedAction: 'toggle', resource },
        });
        // readOnly: visible but non-mutating. No state change, no
        // onCheckedChange call, no onClick forward.
        return;
      }

      const next = !current;
      if (!isControlled) setInternal(next);

      emit({
        component: 'toggle',
        action: CANONICAL_ACTIONS.TOGGLE_CHANGE,
        permissionResult: 'granted',
        details: { from: current, to: next },
      });

      onCheckedChange?.(next);
      onClick?.(event);
    };

    const permissionDataState =
      !granted && mode !== 'hidden' ? 'denied' : undefined;

    const resolvedTitle =
      rest.title ?? (permissionDataState && denyReason ? denyReason : undefined);

    return (
      <button
        {...rest}
        ref={ref}
        type="button"
        role="switch"
        aria-checked={current}
        disabled={isDisabled}
        aria-disabled={isDisabled || undefined}
        data-oshon-primitive="toggle"
        data-state={current ? 'on' : 'off'}
        data-permission-state={permissionDataState}
        title={resolvedTitle}
        onClick={handleClick}
      >
        {children}
      </button>
    );
  },
);
